import {
    PsHttpService,
    type PatSnapCommonResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { currentClientUUID } from '@patsnap/uniform-setting';
import { isUserLogin } from '@eureka/utils/account';
import type { InputExample } from '@/domains/home/apis/home-api';
import type { PdaScenarioExample, ScenarioType } from '@/types/pda';

export interface AgentItem {
    short_name: string;
    name: string;
    icon_url?: string;
    goal?: string;
    input_examples?: string[];
    type?: 'agent' | 'playbook' | 'roadmap';
    function_id?: string;
    feature_id?: string;
    order?: number;
}

export interface LsHomeAgentCard {
    goal: string;
    icon_url?: string;
    name: string;
    order: number;
    short_name: string;
    key: 'eureka_ls_agent_generic_ip_launch_check' | 'eureka_ls_agent_patent_preclinical_candidate_compound';
    function_id: string;
    agent_id: number;
    type: 'playbook' | 'agent' | 'roadmap';
    feature_id: string;
    // 2025-07-22
    published_date?: string;
    charge_mode?: 'COMMERCIAL' | 'FREEMIUM' | 'TRIAL'

    example?: InputExample[]
}

export interface LsHomeAgentGroup {
    agents: LsHomeAgentCard[];
    name: string;
    order: number;
}

export type LsHomePurchasedAgentData = Record<string, LsHomeAgentGroup>
export type LsHomePLGAgentData = {
    plg: LsHomeAgentGroup
}

export type LsHomeAllAgentData = Record<string, {
    groups: Record<string, LsHomeAgentGroup>
    name: string;
    order: number;
}>

export interface HomeLsApi {
    getPurchasedAgentData(): Promise<LsHomePurchasedAgentData & { show_tabs: boolean }>;
    getAllAgentData(): Promise<LsHomeAllAgentData & { common: LsHomeAgentGroup }>;
    getPlgAgentData(): Promise<LsHomePLGAgentData & { show_tabs: boolean }>;
    postLsFeedback(data: FormData): Promise<any>;
    getAvailableAgents(): Promise<AgentItem[]>;
    getPdaScenarioExample(language: string): Promise<PdaScenarioExample[]>;
    pdaUpload(data: Record<string, any>, onProgress?: (progress: number) => void, signal?: AbortSignal): Promise<any>;
    pdaExampleUpload(params: { language: string; file_id: string; file_scenario: ScenarioType }): Promise<{ file_id: string; file_name: string; file_path: string }>;
    extractBatchFile(data: {
        input?: string
        file_ids: string[]
        file_scenario?: ScenarioType
    }): Promise<{ file_group_id: string }>;
}

export const HomeLsApi = Symbol('HomeLsApi');

@injectable()
export class HomeLsApiImpl extends PsHttpService implements HomeLsApi {
    get baseUrl() {
        return `${import.meta.env.VITE_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0`
    }

    get options() {
        return {
            baseURL: `${this.baseUrl}/homepage`,
            headers: {
                'X-Device-ID': currentClientUUID(),
                'X-API-Version': '2.0',
            },
        };
    }

    getPurchasedAgentData() {
        return this.get<PatSnapCommonResponseData<LsHomePurchasedAgentData & { show_tabs: boolean }>>({
            url: '/cards',
            params: {
                type: 'purchased',
            },
        }).then((res) => {
            return res as unknown as LsHomePurchasedAgentData & { show_tabs: boolean };
        });
    }

    getAllAgentData() {
        return this.get<PatSnapCommonResponseData<LsHomeAllAgentData & { common: LsHomeAgentGroup }>>({
            url: '/cards',
            params: {
                type: 'all',
            },
        }).then((res) => {
            return res as unknown as LsHomeAllAgentData & { common: LsHomeAgentGroup };
        });
    }

    getPlgAgentData() {
        return this.get<PatSnapCommonResponseData<LsHomePLGAgentData & { show_tabs: boolean }>>({
            url: '/cards',
            params: {
                type: 'plg',
            },
        }).then((res) => {
            return res as unknown as LsHomePLGAgentData & { show_tabs: boolean };
        })
    }

    postLsFeedback(data: FormData) {
        return this.post({
            url: '/feed',
            data,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
    }

    async getAvailableAgents(): Promise<AgentItem[]> {
        const isLogin = await isUserLogin()
        const base = isLogin ? '' : '/guest'
        return this.get<PatSnapCommonResponseData<AgentItem[]>>({ url: `${base}/homepage/agents/available`, baseURL: this.baseUrl })
            .then(res => res as unknown as AgentItem[])
    }

    async getPdaScenarioExample(language: string): Promise<PdaScenarioExample[]> {
        const isLogin = await isUserLogin()
        const base = isLogin ? '' : '/guest'
        return this.get<PatSnapCommonResponseData<PdaScenarioExample[]>>({
            baseURL: this.baseUrl,
            url: `${base}/file/examples`,
            params: { language },
        }).then(res => res as unknown as PdaScenarioExample[])
    }

    async pdaUpload(data: Record<string, any>, onProgress?: (progress: number) => void, signal?: AbortSignal): Promise<any> {
        const isLogin = await isUserLogin()
        const base = isLogin ? '' : '/guest'
        const formData = new FormData()
        for (const key in data) {
            formData.append(key, data[key])
        }
        return this.post<PatSnapCommonResponseData<any>>({
            baseURL: this.baseUrl,
            url: `${base}/file/upload`,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 1000 * 60 * 5,
            signal,
            onUploadProgress: (progressEvent: any) => {
                if (progressEvent.total && onProgress) {
                    onProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100))
                }
            },
        }).then(res => ({ success: true, data: res }))
    }

    async pdaExampleUpload(params: { language: string; file_id: string; file_scenario: ScenarioType }): Promise<{ file_id: string; file_name: string; file_path: string }> {
        const isLogin = await isUserLogin()
        const base = isLogin ? '' : '/guest'
        return this.get<PatSnapCommonResponseData<{ file_id: string; file_name: string; file_path: string }>>({
            baseURL: this.baseUrl,
            url: `${base}/file/example/upload`,
            params,
        }).then(res => res as unknown as { file_id: string; file_name: string; file_path: string })
    }

    /**
   * 批量提取文件
   * @param params 批量提取参数
   * @returns 文件组ID
   */
    async extractBatchFile(data: {
        input?: string
        file_ids: string[]
        file_scenario?: ScenarioType
    }) {
        const isLogin = await isUserLogin()
        const base = isLogin ? '' : '/guest'
        return this.post({
            url: base + '/file/extract-batch',
            data,
            baseURL: this.baseUrl,
        }) as unknown as Promise<{ file_group_id: string }>
    }
}
